import { isBusy } from './activity.js';

/**
 * Connected-Swarasense (W8): auto-reload on a new version, when idle. Called
 * once from `main.ts`'s `onNeedRefresh` (a new service worker is waiting).
 * Instead of the old one-tap "Refresh" toast, this applies the update
 * automatically — but never mid-take/mid-finder: if the app is busy it waits,
 * polling, and reloads the moment the user is idle. A brief "Updating…" flash
 * shows just before the reload so it isn't jarring.
 *
 * `apply` is `updateSW(true)` (skipWaiting + reload) — the caller owns HOW the
 * update is applied; this module owns WHEN.
 */

const FLASH_ID = 'update-flash';
/** How often to re-check for idle while a reload is deferred (ms). */
const IDLE_POLL_MS = 3000;
/** How long the "Updating…" flash sits before the reload (ms) — long enough to
 * read, short enough not to feel like a hang. */
const FLASH_MS = 900;

function reducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function flashThenApply(apply: () => void): void {
  if (typeof document !== 'undefined' && !document.getElementById(FLASH_ID)) {
    const el = document.createElement('div');
    el.id = FLASH_ID;
    el.className = 'update-flash';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.textContent = 'Updating to the latest version…';
    document.body.appendChild(el);
  }
  // A tiny beat so the flash paints (and screen readers announce it) before the
  // reload navigates away; instant under reduced-motion is fine too, but keep a
  // small delay so the status is still announced.
  window.setTimeout(apply, reducedMotion() ? 250 : FLASH_MS);
}

/**
 * Apply the update now if idle, else defer until the app becomes idle. Safe to
 * call more than once (a second `onNeedRefresh` while already armed just starts
 * another poller that no-ops once the first reload navigates away).
 */
export function armAutoUpdate(apply: () => void): void {
  if (!isBusy()) {
    flashThenApply(apply);
    return;
  }
  const iv = window.setInterval(() => {
    if (!isBusy()) {
      window.clearInterval(iv);
      flashThenApply(apply);
    }
  }, IDLE_POLL_MS);
}
